import { Injectable } from "@angular/core";
import { Auth, EmailAuthProvider, User, applyActionCode, authState, confirmPasswordReset, createUserWithEmailAndPassword, onAuthStateChanged, reauthenticateWithCredential, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, updateEmail, updatePassword, updateProfile, user, verifyPasswordResetCode } from "@angular/fire/auth";
import { BehaviorSubject, Observable, Subscription, firstValueFrom } from "rxjs";
import { ProfileModel } from "src/app/models/profile.model";
import { UserModel } from "../../models/user.model";
import { DataService } from "../data/data.service";

/*
  Generated class for the DataServiceProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular DI.
*/
@Injectable({ providedIn: "root" })
export class AuthenticationService {
  syncHandler: any;
  _user: UserModel;
  authenticationState = new BehaviorSubject(false);

  public mode = "online";
  // private auth: Auth = inject(Auth);
  public authState$ = authState(this.auth);

  public currentUser$:Observable<UserModel> = new Observable((currentUser) =>{
    onAuthStateChanged(this.auth, (user) => {
      if (user) {
        currentUser.next(user);
        currentUser.complete();
      }
    });
    return {
      unsubscribe() {
        onAuthStateChanged
      }
    };
  })
  authStateSubscription: Subscription;


  constructor(
    private dataService: DataService, private auth: Auth
  ) {
    this.authStateSubscription = this.authState$.subscribe(async (user: User | null) => {
        // console.log('authentication service authstate',user);
        if (user) {
          // console.log(result);
          this._user = user;
          this.authenticationState.next(true);
        }
      });
      onAuthStateChanged(this.auth, async (user) => {
        // console.log('authentication service authstate changed',user);
        this.setUser(user);
      });
  }

  workOffline(): void {
    // console.log("workoffline");
    this.mode = "offline";
  }

  async login(user: UserModel): Promise<UserModel> {
    return new Promise(async (resolve, reject) => {
      try{
        // console.log('login');
        // try{
        //   const persistence: Persistence = indexedDBLocalPersistence; //browserLocalPersistence
        //   await this.auth.setPersistence(persistence);
        //   console.log('local persistence',persistence);
        // }
        // catch(e){
        //   const persistence: Persistence = inMemoryPersistence;
        //   await this.auth.setPersistence(persistence);
        //   console.log('session persistence',persistence);
        // }      
        // const persistence = await this.auth.setPersistence({type:"LOCAL"} as Persistence);
        // console.log('persistence');
        const result = await signInWithEmailAndPassword(this.auth, user.email, user.password);
        await this.setUser(result.user);
        this.authenticationState.next(true);
        resolve(this._user);
      }
      catch (e){
        console.log(e);
        reject(e);
      }
    })
  }

  async reauthenticate(password:string): Promise<void> {
    const credential = EmailAuthProvider.credential(this.auth.currentUser.email,password);
    return new Promise(async (resolve, reject) => {
      const currentUser = await this.auth.currentUser;
      reauthenticateWithCredential(this.auth.currentUser,credential)
        .then(async result => {
          // console.log(result);
          this.authenticationState.next(true);
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  async getUser():Promise<UserModel>{
    // console.log('get user');
    if(this._user && this._user.role){
      // console.log('return local user',this._user);
      return Promise.resolve(this._user);
    }
    else{
      const currentUser = await firstValueFrom(user(this.auth));
      await this.setUser(currentUser);
      return Promise.resolve(this._user);
    }
  }

  async setUser(user:User){
    if(user){
      this._user = user;
      const idTokenResult = await user.getIdTokenResult();
      this._user.role = idTokenResult.claims.role;
      this._user.producers = await firstValueFrom(this.dataService.getMyProducers(this._user.uid));
      if(this._user.producers){
        for(const producer of this._user.producers as ProfileModel[]){
          const name = await firstValueFrom(this.dataService.getProducerName(producer.key));
          producer.details = {
            shortDetails:{
              name: name
            }
          }
        }
      }
    }
    else{
      this._user = null;
    }
  }


  addToPermissions(id:string,type:string,role:string){
    if(type==='producer'){
      if(!this._user.producers){
        this._user.producers = {};
      }
      this._user.producers[id]= {
        role:role
      }
    }
  }
  setRole(role:string){
    this._user.role = role;
  }

  async register(user: UserModel) {
    try {
      const result = await createUserWithEmailAndPassword(this.auth, user.email, user.password);
      if (result) {
        this._user = result.user;
        this._user.role = user.role;
        this.authenticationState.next(true);
        return result;
      }
    } catch (e) {
      console.log(e);
      throw e;
    }
  }
  async logout() {
    try {
      const result = await this.auth.signOut();
      this._user = null;
      this.authenticationState.next(false);
      return result;
    } catch (e) {
      throw e;
    }
  }

  async changeEmail(email) {
    try {
      const currentUser = this.auth.currentUser
      await updateEmail(currentUser,email);
      this._user.email = email;
      return currentUser;
    } catch (e) {
      throw e;
    }
  }

  async changePassword(password) {
    try {
      
      const currentUser = this.auth.currentUser
      updatePassword(currentUser,password);
      this._user.password = password;
      return currentUser;
    } catch (e) {
      throw e;
    }
  }

  async renewPassword(email) {
    try {
      const result = await sendPasswordResetEmail(this.auth,email);
      return result;
    } catch (e) {
      throw e;
    }
  }

  async verifyPasswordReset(code) {
    try {
      const result = await verifyPasswordResetCode(this.auth,code);
      return result;
    } catch (e) {
      throw e;
    }
  }

  async resetPassword(code, password) {
    try {
      const result = await confirmPasswordReset(this.auth, code, password);
      return result;
    } catch (e) {
      throw e;
    }
  }
  async SendEmailVerification() {
    try {
      const currentUser = this.auth.currentUser
      sendEmailVerification(currentUser);
      return currentUser;
    } catch (e) {
      throw e;
    }
  }

  async confirmEmail(code: string): Promise<void> {
    const result = await applyActionCode(this.auth, code);
    return result;
  }

  async isAuthenticated(): Promise<boolean> {
    return !! this.auth.currentUser;
  }

  isLoggedIn() { 
    return this.auth.currentUser;
  }

  async isEmailVerified(): Promise<boolean> {
    const currentUser = this.auth.currentUser;
    // console.log(currentUser);
    return  currentUser? currentUser.emailVerified : false;
  }

  async updateProfilePhoto(url: string): Promise<void> {
    const currentUser = this.auth.currentUser;
    return updateProfile(currentUser,{ photoURL: url });
  }
  async updateDisplayName(name: string): Promise<void> {
    const currentUser = this.auth.currentUser;
    return updateProfile(currentUser,{ displayName: name });
  }
}
